import { ExperimentStore } from 'bernie-core';

import { BadgeAdapter } from 'stores/adapters/bex-api/search';
import { createSearchAndFilterDisplayOptions } from 'stores/adapters/bex-api/search/search-sort-filter-adapter';
import { mapMessageModel } from 'src/common/stores/adapters/bex-api/search/search-results-adapter';

import { BadgeModel, HotelMapModel, MapMarker, SheetModel } from 'models';
import { MarkerIcon } from 'models/hotel/map-types/map-marker';

import { updateSlotsParamForMapTravelAds } from 'utils/infosite-url-utils';

import { LodgingPwaPropertySearch, MapIconType, PropertyFields, Theme } from '__generated__/api';

export const mapMap = (query: LodgingPwaPropertySearch.Query, experiments?: ExperimentStore): HotelMapModel | null => {
  const { map, sortAndFilter } = query.propertySearch;

  if (!map) {
    return null;
  }

  const isSrpMapTravelAdsExperiment = experiments?.exposures.srp_map_travelads?.bucket === 1;

  return {
    ...map,
    filterDisplayOptions: createMapFilterDisplayOptions(sortAndFilter),
    messagingModel: map.messagingModel && mapMessageModel(map.messagingModel, 'MessageResult'),
    markers: getMapMarkers(query as LodgingPwaPropertySearch.Query, isSrpMapTravelAdsExperiment),
    showSearchButton: false,
    searchMessage: null,
    ...adaptMapBounds(query),
  };
};

const adaptMapBounds = (query: LodgingPwaPropertySearch.Query): any => {
  const { map } = query.propertySearch;
  const ne = map.bounds && map.bounds.northeast;
  const sw = map.bounds && map.bounds.southwest;

  return {
    center: {
      lat: map.center && map.center.latitude,
      lng: map.center && map.center.longitude,
    },
    bounds: {
      ne: {
        lat: ne && ne.latitude,
        lng: ne && ne.longitude,
      },
      sw: {
        lat: sw && sw.latitude,
        lng: sw && sw.longitude,
      },
    },
  };
};

export const createMapFilterDisplayOptions = (sortAndFilter?: LodgingPwaPropertySearch.SortAndFilter): any => {
  const mapFiltersBlackList = ['sort'];
  const mapFilters = Object.assign({}, createSearchAndFilterDisplayOptions(sortAndFilter));

  mapFilters.filters.displayFields = mapFilters.filters.displayFields.filter(item => !mapFiltersBlackList.includes(item));

  return mapFilters;
};

const getMapMarkers = (query: LodgingPwaPropertySearch.Query, isSrpMapTravelAdsExperiment?: boolean): MapMarker[] => {
  const { map, propertySearchListings } = query.propertySearch;
  const mapMarkers = map && map.markers;

  const poiMarkers = getMapPoiMarkers(mapMarkers);
  const propertyMarkers = getListingPropertyMarkers(propertySearchListings, isSrpMapTravelAdsExperiment);

  if (propertyMarkers) {
    return propertyMarkers.concat(poiMarkers);
  }

  return poiMarkers;
};

const getSheet = (property: PropertyFields.Fragment): SheetModel | null => {
  let sponsoredBadge: BadgeModel = null;
  let badge: BadgeModel;
  if (property.offerBadge?.primary?.theme_temp === Theme.SPONSORED) {
    sponsoredBadge = BadgeAdapter.getBadge(property?.offerBadge?.primary);
    badge = BadgeAdapter.getBadge(property?.offerBadge?.secondary);
  } else {
    const primaryBadge = property?.offerBadge?.primary;
    const secondaryBadge = property?.offerBadge?.secondary;
    badge = primaryBadge ? BadgeAdapter.getBadge(primaryBadge) : BadgeAdapter.getBadge(secondaryBadge);
  }

  const linkOff = property.sponsoredListing
    ? updateSlotsParamForMapTravelAds(property.propertyDetailsLink?.uri?.relativePath)
    : property.propertyDetailsLink?.uri?.relativePath;

  const price = property.price;
  const lead = price && price.lead && price.lead.formatted;

  return {
    primaryBadge: sponsoredBadge,
    secondaryBadgeText: badge.text,
    secondaryBadgeTheme: badge.theme,
    distanceFromPOI: property.destinationInfo && property.destinationInfo.distanceFromMessaging,
    id: Number(property.id),
    leadPrice: property.mapMarker.icon === MapIconType.SOLD_OUT ? null : lead,
    linkOff,
    photoUrl: property?.propertyImage?.image?.url,
    priceMessages: property.price && property.price.priceMessages && property.price.priceMessages.map(message => message.value),
    title: property.name,
    strikeoutPrice: price && price.strikeOut && price.strikeOut.formatted,
    strikeoutDisclaimer: price && price.disclaimer && price.disclaimer.value,
    subtitle: property.reviews && property.reviews.localizedSubtitle,
    vipMessage: property.vipMessaging,
    options: price && price.options,
    travelAd: property.sponsoredListing,
  };
};

const getMapPoiMarkers = (mapMarkers: LodgingPwaPropertySearch.Markers[]): MapMarker[] => {
  const poiMarkers = mapMarkers && mapMarkers.filter(marker => marker.__typename === 'PointOfInterest');
  if (!poiMarkers || poiMarkers.length === 0) {
    return null;
  }

  return poiMarkers.map(marker => ({
    position: {
      lat: marker.mapMarker.latLong?.latitude,
      lng: marker.mapMarker.latLong?.longitude,
    },
    label: marker.mapMarker.label,
    icon: MarkerIcon[marker.mapMarker.icon],
    sheet: { title: marker.name, id: -1 },
  }));
};

const getListingPropertyMarkers = (
  propertySearchListings: LodgingPwaPropertySearch.PropertySearchListings[],
  isSrpMapTravelAdsExperiment?: boolean
): MapMarker[] => {
  const properties = getPropertiesForMapMarkers(propertySearchListings, isSrpMapTravelAdsExperiment);

  if (!properties || properties.length === 0) {
    return null;
  }

  return properties
    .filter(property => property.mapMarker)
    .map(property => ({
      position: {
        lat: property.mapMarker.latLong.latitude,
        lng: property.mapMarker.latLong.longitude,
      },
      label: property.mapMarker.label,
      icon: MarkerIcon[property.mapMarker.icon],
      sheet: getSheet(property),
    }));
};

const getPropertiesForMapMarkers = (
  listings: LodgingPwaPropertySearch.PropertySearchListings[],
  isSrpMapTravelAdsExperiment?: boolean
): PropertyFields.Fragment[] => {
  const properties = getPropertiesFromListings(listings);

  if (!isSrpMapTravelAdsExperiment) {
    return properties && properties.filter(property => !property.sponsoredListing);
  }

  const propertyIds = getPropertyIdsWithTravelAdsLinkin(properties);

  return (
    properties &&
    properties.filter(
      property =>
        (property.sponsoredListing && property.sponsoredListing.clickRedirectLinkoffTrackingUrl === null) ||
        (!property.sponsoredListing && !propertyIds.includes(property.id))
    )
  );
};

const getPropertiesFromListings = (listings: LodgingPwaPropertySearch.PropertySearchListings[]): PropertyFields.Fragment[] =>
  listings &&
  listings
    .filter(listing => listing.__typename === 'Property' && !isZeroCoordinates(listing))
    .map(listing => listing as PropertyFields.Fragment);

const getPropertyIdsWithTravelAdsLinkin = (properties: PropertyFields.Fragment[]): string[] =>
  properties
    .filter(property => property.sponsoredListing && !property.sponsoredListing.clickRedirectTrackingUrl)
    .map(property => property.id);

const isZeroCoordinates = (property: PropertyFields.Fragment): boolean =>
  property.mapMarker?.latLong.latitude === 0 && property.mapMarker?.latLong.longitude === 0;
